import { useAtom } from "jotai";
import { ToastAction } from "~/components/ui/toast";
import { toast } from "~/hooks/use-toast";
import logger from "~/lib/logger";
import {
  toggleIsAutoPauseEnabledAction,
  toggleIsMiniPlayerHiddenAction,
} from "~/server/actions/videos/actions";

import { isAutoPauseEnabledAtom, isMiniPlayerHiddenAtom } from "../state";

export function useVideoControls() {
  const [isAutoPauseEnabled, setIsAutoPauseEnabled] = useAtom(
    isAutoPauseEnabledAtom,
  );
  const [isMiniPlayerHidden, setIsMiniPlayerHidden] = useAtom(
    isMiniPlayerHiddenAtom,
  );

  const toggleIsMiniPlayerHidden = async (noToast = false) => {
    try {
      const result = await toggleIsMiniPlayerHiddenAction();

      if (!result.success) {
        showToastForError(result.message);
        return;
      }

      if (!result.isSignedIn) {
        setIsMiniPlayerHidden(!isMiniPlayerHidden);
        if (noToast) return;
        showToastForMiniPlayer(!isMiniPlayerHidden, toggleIsMiniPlayerHidden);
      } else {
        setIsMiniPlayerHidden(result.isMiniPlayerHidden ?? false);
        if (noToast) return;
        showToastForMiniPlayer(
          result.isMiniPlayerHidden ?? false,
          toggleIsMiniPlayerHidden,
        );
      }
    } catch (error) {
      logger.error("Error toggling mini player", { error });
    }
  };

  const toggleIsAutoPauseEnabled = async () => {
    try {
      const result = await toggleIsAutoPauseEnabledAction();

      if (!result.success) {
        showToastForError(result.message);
        return;
      }

      if (!result.isSignedIn) {
        setIsAutoPauseEnabled(!isAutoPauseEnabled);
        showToastForAutoPause(!isAutoPauseEnabled);
      } else {
        setIsAutoPauseEnabled(result.isAutoPauseEnabled ?? false);
        showToastForAutoPause(result.isAutoPauseEnabled ?? false);
      }
    } catch (error) {
      logger.error("Error toggling auto pause", { error });
    }
  };

  return {
    isAutoPauseEnabled,
    isMiniPlayerHidden,
    toggleIsAutoPauseEnabled,
    toggleIsMiniPlayerHidden,
  };
}

const showToastForAutoPause = (isAutoPauseEnabled: boolean) => {
  toast({
    duration: 3_000,
    title: isAutoPauseEnabled ? "Auto Pause Enabled" : "Auto Pause Disabled",
    description: isAutoPauseEnabled
      ? "The video will now automatically pause after you click play on a word."
      : "The video will not automatically pause.",
  });
};

const showToastForMiniPlayer = (
  isMiniPlayerHidden: boolean,
  toggleIsMiniPlayerHidden?: (noToast?: boolean) => Promise<void>,
) => {
  toast({
    duration: 3_000,
    title: isMiniPlayerHidden
      ? "Mini Player Hidden"
      : "Mini Player Now Showing",
    description: isMiniPlayerHidden
      ? "The mini player won't be shown as you scroll."
      : "The mini player will be shown as you scroll.",
    action:
      isMiniPlayerHidden && toggleIsMiniPlayerHidden ? (
        <ToastAction
          altText="Undo"
          onClick={() => void toggleIsMiniPlayerHidden(true)}
        >
          Undo
        </ToastAction>
      ) : undefined,
  });
};

const showToastForError = (message?: string | null) => {
  toast({
    variant: "destructive",
    title: "Error",
    description: message ?? "An error occurred",
  });
};
